import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";

import { ScheduleCatalogService } from "../../../services/v2/scheduleCatalog.service";
import { plano } from "./salesHelpers";

const catalogo = new ScheduleCatalogService();
const cache: { [campus: number]: Promise<any> } = {};
const catalogoDe = (campusId: number): Promise<any> => {
	if (!cache[campusId]) cache[campusId] = catalogo.catalogue(campusId).then((r) => r.data || {}).catch((e) => { delete cache[campusId]; throw e; });
	return cache[campusId];
};

/** El mismo formato que "Horarios abiertos" de la ficha. */
export const textoHorarios = (hs: any[]): string => hs.map((h) =>
	`*Diplomado:* ${h.curso}\n*Periodo:* ${h.periodo}\n*Horario:* ${h.horario}\nInicia: ${h.inicioTexto}`).join("\n\n");

/** Vista previa con el formato de WhatsApp: *negritas*, _cursivas_, ~tachado~ y saltos de línea. */
export const formatoWa = (t: string): React.ReactNode[] => t.split("\n").map((linea, i) => {
	const partes: React.ReactNode[] = [];
	// Como WhatsApp: la marca abre al inicio o tras un no-letra y cierra antes de un no-letra (nombre_de_usuario no es cursiva).
	// Sin lookbehind: Safari viejo del iPhone no lo entiende y tumbaría la conversación.
	const re = /(^|[^0-9A-Za-zÀ-ÿ])(\*[^*\n\s](?:[^*\n]*[^*\n\s])?\*|_[^_\n\s](?:[^_\n]*[^_\n\s])?_|~[^~\n\s](?:[^~\n]*[^~\n\s])?~)(?=[^0-9A-Za-zÀ-ÿ]|$)/g;
	let ult = 0, m: RegExpExecArray | null, k = 0;
	while ((m = re.exec(linea))) {
		const ini = m.index + m[1].length;
		if (ini > ult) partes.push(linea.slice(ult, ini));
		const x = m[2], dentro = x.slice(1, -1);
		partes.push(x[0] === "*" ? <b key={k++}>{dentro}</b> : x[0] === "_" ? <i key={k++}>{dentro}</i> : <s key={k++}>{dentro}</s>);
		ult = ini + x.length;
	}
	if (ult < linea.length) partes.push(linea.slice(ult));
	return <React.Fragment key={i}>{partes}{i < t.split("\n").length - 1 ? <br /> : null}</React.Fragment>;
});

interface Props {
	campusId: number;
	/** Plantel de la persona (si no, el de la barra). */
	plantel: number;
	/** Lo que le interesa, para empezar ya filtrado. */
	curso: string | null;
	/** Con la ventana abierta se manda directo; si no, solo se puede poner en el cuadro. */
	puedeEnviar: boolean;
	onEnviar: (texto: string) => Promise<void>;
	onAlCuadro: (texto: string) => void;
	onClose: () => void;
}

/**
 * Enviar horarios (Esteban, 2026-10-05: "que abra un modalsito del catálogo de horarios y se envíe, easy peasy").
 * Plantel, buscar curso, tocar los horarios y Enviar. Por defecto solo los que están por comenzar.
 * 6-oct: antes de mandar se ve el texto como le llega en WhatsApp y se puede editar ("ver cómo se ve y medio editar").
 */
const HorariosModal: React.FC<Props> = ({ campusId, plantel: plantelInicial, curso, puedeEnviar, onEnviar, onAlCuadro, onClose }) => {
	const [datos, setDatos] = useState<any | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [plantel, setPlantel] = useState<number>(plantelInicial);
	const [busca, setBusca] = useState<string>(() => (curso || "").replace(/^Diplomado (de|en) /i, "").split(/[/,]/)[0].trim());
	const [todos, setTodos] = useState<boolean>(false);
	const [marcados, setMarcados] = useState<number[]>([]);
	const [enviando, setEnviando] = useState<boolean>(false);
	const [paso, setPaso] = useState<"elegir" | "revisar">("elegir");
	const [texto, setTexto] = useState<string>("");
	useEffect(() => { catalogoDe(campusId).then(setDatos).catch(() => setError("No se pudo abrir el catálogo de horarios.")); }, [campusId]);

	const lista = useMemo(() => {
		const ws = plano(busca).split(/\s+/).filter((w) => w.length >= 2);
		return ((datos?.horarios || []) as any[])
			.filter((h) => h.plantel === plantel && (todos || h.estado === "por-comenzar"))
			.filter((h) => !ws.length || ws.every((w) => plano(h.curso + " " + h.cursoFull + " " + h.modulo + " " + h.horario).includes(w)))
			.sort((a, b) => String(a.curso).localeCompare(String(b.curso)) || String(a.inicio).localeCompare(String(b.inicio)))
			.slice(0, 120);
	}, [datos, plantel, busca, todos]);
	const elegidos = ((datos?.horarios || []) as any[]).filter((h) => marcados.includes(h.id));
	const alternar = (id: number) => setMarcados((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
	const revisar = () => { setTexto(textoHorarios(elegidos)); setPaso("revisar"); };
	const enviar = async () => {
		if (!texto.trim()) return;
		setEnviando(true); setError(null);
		try { await onEnviar(texto.trim()); onClose(); }
		catch (e: any) { setError(e?.response?.data?.message || e?.message || "No se pudo enviar."); }
		finally { setEnviando(false); }
	};

	let cursoAnterior = "";
	return (
		<Modal show onHide={onClose} size="lg" className="ventas-nuevo-modal hm-modal" fullscreen="sm-down">
			<Modal.Header closeButton>
				<Modal.Title style={{ fontFamily: "Bricolage, sans-serif", fontWeight: 800 }}>{paso === "revisar" ? "Revisa el mensaje" : "Enviar horarios"}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{paso === "revisar" ? (
					<div className="hm-revisar">
						{error ? <div className="aviso error">{error}</div> : null}
						<div className="hm-previa" aria-label="Así le llega por WhatsApp">
							<div className="hm-previa-tit">Así le llega</div>
							<div className="hm-burbuja">{texto.trim() ? formatoWa(texto) : <span className="apagado">El mensaje está vacío.</span>}</div>
						</div>
						<label className="hm-editar">Puedes editarlo (agregar un saludo, quitar una línea…)
							<textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={Math.min(16, Math.max(6, texto.split("\n").length + 1))} />
						</label>
						<div className="hm-ayuda">*texto* sale en negritas y _texto_ en cursiva, como en WhatsApp.</div>
					</div>
				) : <>
				<div className="hm-filtros">
					<select value={plantel} onChange={(e) => { setPlantel(Number(e.target.value)); setMarcados([]); }} aria-label="Plantel">
						{(datos?.planteles || []).map((p: any) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
					</select>
					<input type="text" enterKeyHint="search" placeholder="Curso (barbería, uñas, cocina…)" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Curso" />
				</div>
				<label className="hm-todos"><input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} /> Incluir los que ya empezaron</label>
				{error ? <div className="aviso error">{error}</div> : null}
				{/* Lo marcado siempre a la vista, aunque el buscador lo esconda: antes se mandaba sin verse (barrido 26-oct, C12). */}
				{elegidos.length ? (
					<div className="hm-elegidos" aria-label="Horarios elegidos">
						{elegidos.map((h) => (
							<span key={h.id} className="hm-chip">{h.curso} · {h.horario}
								<button type="button" onClick={() => alternar(h.id)} aria-label={"Quitar " + h.curso + " " + h.horario}>×</button></span>
						))}
					</div>
				) : null}
				<div className="hm-lista">
					{!datos && !error ? <div className="apagado">Cargando horarios…</div> : null}
					{datos && !lista.length ? <div className="apagado">No hay horarios {todos ? "" : "por comenzar "}con esa búsqueda en este plantel.</div> : null}
					{lista.map((h) => {
						const cab = h.curso !== cursoAnterior ? <div className="hm-curso" key={"c" + h.id}>{h.curso}</div> : null;
						cursoAnterior = h.curso;
						const on = marcados.includes(h.id);
						return (
							<React.Fragment key={h.id}>
								{cab}
								<button type="button" className={"hm-fila" + (on ? " on" : "")} aria-pressed={on} onClick={() => alternar(h.id)}>
									<span className="hm-check" aria-hidden="true">{on ? "✓" : ""}</span>
									<span className="hm-tx">
										<b>{h.horario}</b>
										<span>{h.modulo && h.modulo !== h.curso ? h.modulo + " · " : ""}inicia {h.inicioTexto}{h.estado === "en-curso" ? " · ya empezó" : ""}
											{lista.some((o) => o.id !== h.id && o.curso === h.curso && o.horario === h.horario && o.inicio === h.inicio) ? " · Horario #" + h.id : ""}</span>
									</span>
								</button>
							</React.Fragment>
						);
					})}
				</div>
				</>}
			</Modal.Body>
			<Modal.Footer className="hm-pie">
				{paso === "revisar" ? (
					<>
						<button className="btn-wa" onClick={() => setPaso("elegir")} style={{ marginRight: "auto" }}>← Cambiar horarios</button>
						<button className="btn-wa" disabled={!texto.trim()} onClick={() => { onAlCuadro(texto.trim()); onClose(); }}>Poner en el mensaje</button>
						<button className="btn-wa pri hm-enviar" disabled={!texto.trim() || !puedeEnviar || enviando} onClick={enviar}
							title={puedeEnviar ? "" : "Pasaron 24 h desde su último mensaje: solo plantilla"}>
							{enviando ? "Enviando…" : "Enviar"}
						</button>
					</>
				) : (
					<button className="btn-wa pri hm-enviar" disabled={!elegidos.length} onClick={revisar}>
						{"Revisar mensaje" + (elegidos.length ? " (" + elegidos.length + ")" : "")} →
					</button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default HorariosModal;
